/**
 * The wiring behind „Do przejrzenia": the queue, the submission open in the inbox, the keyboard that
 * walks it, and the one write.
 *
 * ── An INBOX since 4 November 2026 ───────────────────────────────────────────────────────────
 * Owner: the list and the report dialog became a master-detail inbox — the queue on the left, the
 * open submission on the right, „Przejrzane, dalej" marking it and moving on. There is always an
 * open submission while the queue holds one; `model/reviewInbox` resolves the picked id against the
 * rows, so a row that leaves the list never stays open.
 *
 * ── ONE queue ────────────────────────────────────────────────────────────────────────────────
 * The kind filter went on 17 September 2026, so this hook takes no argument and the read names no
 * kind: the backend answers with every submission waiting.
 *
 * ── The EXPOSED state is the wait branch, tail and all ───────────────────────────────────────
 * `listState` says „pending" for as long as the gate says the wait belongs on screen, which outlasts
 * the read by the skeleton's 300ms minimum life. The screen forks on it and so forks on the gate.
 *
 * ── The keyboard is the window's, and the decision is the model's ────────────────────────────
 * J, K and E are bound on `window` while the tab is mounted. Whether a press is the inbox's is
 * `inboxCommand`'s call; this hook only reads the DOM facts it needs — is focus typing, or inside a
 * menu or a dialog — and hands them in.
 *
 * ── The mark is `features/survey-review`'s, unchanged ────────────────────────────────────────
 * Its callback runs before the refetch removes the row, which is where the selection is moved to
 * the successor and the sentence is published.
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { clientSurveysQueryKey } from '~/features/survey-report';
import { useMarkSurveyReviewed } from '~/features/survey-review';
import { isTextEntry } from '~/shared/lib/isTextEntry';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { tabPanelId } from '~/shared/ui/TabStrip';

import { reviewQueueQueryKey, reviewQueueQueryOptions } from '../api/reportsQueries';
import { inboxCommand, openedId, pinnedId, steppedId, successorId } from '../model/reviewInbox';
import {
  reviewQueue,
  windowSentence,
  type ReviewQueueView,
  type ReviewRow,
} from '../model/reviewQueue';

/** What the inbox is showing. */
export type ReviewListState = 'pending' | 'error' | 'empty' | 'rows';

export interface ReviewTabView {
  /** What the inbox draws. „pending" covers the whole gated wait, not only the open read. */
  readonly listState: ReviewListState;
  readonly listSkeleton: boolean;
  readonly listReveal: string;
  readonly queue: ReviewQueueView;
  /** „Ostatnie 30 dni", from the response's own field. `null` before it answers. */
  readonly windowNote: string | null;
  /** The submission open in the pane. `null` only while the queue holds nothing. */
  readonly opened: ReviewRow | null;
  readonly previousId: number | null;
  readonly nextId: number | null;
  readonly onOpen: (assignmentId: number) => void;
  /** Marks the open submission reviewed and opens the one after it. */
  readonly onReviewNext: () => void;
  readonly marking: boolean;
  readonly markFailed: boolean;
  readonly onRetry: () => void;
}

/** The id a row's own button carries, so a keyboard step can bring it into view. */
export function reviewRowId(assignmentId: number): string {
  return `review-row-${String(assignmentId)}`;
}

/** Focus is somewhere a letter means a letter, or an overlay owns the keyboard. */
function keyboardIsCaptured(target: EventTarget | null): boolean {
  if (isTextEntry(target)) return true;
  return target instanceof Element && target.closest('[role="dialog"], [role="menu"], [role="listbox"]') !== null;
}

export function useReviewTab(announce: (message: string) => void): ReviewTabView {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  /** The submission the trainer picked. Resolved against the list on every render — see the header. */
  const [picked, setPicked] = useState<number | null>(null);

  const queryClient = useQueryClient();
  const queue = useQuery(reviewQueueQueryOptions());
  const view = reviewQueue(queue.data, { t, locale });

  // Pin whatever the pane shows, so a refetch that brings a new submission to the top does not swap
  // it under the trainer (`pinnedId`). Adjusting state during render, React's own pattern for
  // state derived from props: it re-renders once before painting, never loops.
  const pin = pinnedId(view.rows, picked);
  if (pin !== picked) setPicked(pin);

  const currentId = openedId(view.rows, picked);
  const opened = view.rows.find((row) => row.assignmentId === currentId) ?? null;
  const previousId = steppedId(view.rows, currentId, -1);
  const nextId = steppedId(view.rows, currentId, 1);

  const listAnswer: ReviewListState = queue.isPending
    ? 'pending'
    : queue.isError
      ? 'error'
      : view.rows.length > 0
        ? 'rows'
        : 'empty';
  const listGate = useSkeletonGate(listAnswer === 'pending');
  const listState: ReviewListState = listGate.waiting ? 'pending' : listAnswer;

  const review = useMarkSurveyReviewed({ invalidates: [reviewQueueQueryKey] });
  /**
   * A mark in flight. `review.marking` only turns true on the NEXT render, so a second E pressed
   * before it would post the same submission twice; the ref answers at once.
   */
  const marking = useRef(false);

  const open = (assignmentId: number | null) => {
    if (assignmentId === null) return;
    setPicked(assignmentId);
    // A failure said about the previous submission is not about this one.
    review.clearFailure();
    document.getElementById(reviewRowId(assignmentId))?.scrollIntoView({ block: 'nearest' });
  };

  const reviewNext = () => {
    if (opened === null || marking.current) return;
    marking.current = true;
    const successor = successorId(view.rows, opened.assignmentId);
    review.markReviewed(
      opened.assignmentId,
      () => {
        marking.current = false;
        announce(t('web:reports.queue.marked', { client: opened.name, survey: opened.templateTitle }));
        // The pane reads the client's own history, which carries the review stamp too.
        void queryClient.invalidateQueries({ queryKey: clientSurveysQueryKey(opened.customerId) });
        // Before the refetch removes the row, so the pane never falls back to the top of the
        // queue, and focus goes where the trainer's eye goes: the next row, or — once the queue is
        // empty — the tab's own panel, never `<body>`.
        setPicked(successor);
        const target =
          successor === null
            ? document.getElementById(tabPanelId('przegladu'))
            : document.getElementById(reviewRowId(successor));
        target?.focus();
      },
      () => {
        marking.current = false;
      },
    );
  };

  const live = listState === 'rows';
  useEffect(() => {
    if (!live) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const command = inboxCommand({
        key: event.key,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        altKey: event.altKey,
        repeat: event.repeat,
        captured: keyboardIsCaptured(event.target),
      });
      if (command === null) return;
      event.preventDefault();
      if (command === 'next') open(nextId);
      else if (command === 'previous') open(previousId);
      else reviewNext();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  });

  return {
    listState,
    listSkeleton: listGate.skeleton,
    listReveal: listGate.reveal,
    queue: view,
    windowNote: windowSentence(view, t),
    opened,
    previousId,
    nextId,
    onOpen: open,
    onReviewNext: reviewNext,
    marking: review.marking,
    markFailed: review.markFailed,
    onRetry: () => {
      void queue.refetch();
    },
  };
}
